import { ThemeEvents } from '@theme/events';

class UOPurchaseMode extends HTMLElement {
  connectedCallback() {
    if (this.dataset.uoInitialized === 'true') return;
    this.dataset.uoInitialized = 'true';
    this.abortController = new AbortController();
    this.initialize();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    delete this.dataset.uoInitialized;
  }

  async initialize() {
    await Promise.all([
      customElements.whenDefined('product-form-component'),
      customElements.whenDefined('quantity-selector-component'),
      customElements.whenDefined('add-to-cart-component'),
    ]).catch(() => {});

    const section = this.closest('.shopify-section');
    const selector = `product-form-component[data-product-id="${this.dataset.productId}"]`;
    this.formComponent = section?.querySelector(selector) || document.querySelector(selector);
    this.form = this.formComponent?.querySelector('form');
    if (!this.form) return;

    this.variantInput = this.form.querySelector('input[name="id"]');
    this.quantitySelector = this.form.querySelector('quantity-selector-component');
    this.quantityInput = this.quantitySelector?.querySelector('input[ref="quantityInput"]');
    this.addToCartComponent = this.form.querySelector('add-to-cart-component');
    this.addToCartButton = this.addToCartComponent?.querySelector('[ref="addToCartButton"]');
    this.readyRadio = this.querySelector('[data-uo-purchase-mode="ready"]');
    this.dtoRadio = this.querySelector('[data-uo-purchase-mode="dto"]');

    if (!this.variantInput || !this.quantityInput || !this.readyRadio || !this.dtoRadio) return;

    this.readyVariantId = this.dataset.readyVariantId || this.variantInput.value;
    this.dtoVariantId = this.dataset.dtoVariantId;
    this.readyStock = Number(this.dataset.readyStock || 0);
    this.readyTracked = this.dataset.readyTracked === 'true';
    this.dtoMinimum = Number(this.dataset.dtoMinimum || 3);
    this.readyValue = Math.max(1, Number(this.quantityInput.value) || 1);
    this.dtoValue = this.dtoMinimum;
    this.readyCartQuantity = Number(this.quantityInput.dataset.cartQuantity || 0);
    this.currentMode = null;

    this.styleUnavailableReadyChoice();

    const { signal } = this.abortController;
    this.readyRadio.addEventListener('change', () => {
      if (this.readyRadio.checked) this.applyMode('ready');
    }, { signal });
    this.dtoRadio.addEventListener('change', () => {
      if (this.dtoRadio.checked) this.applyMode('dto');
    }, { signal });
    this.quantitySelector?.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      () => requestAnimationFrame(() => this.updateAddToCartLabel()),
      { signal }
    );

    this.applyMode(this.dtoRadio.checked ? 'dto' : 'ready');
  }

  styleUnavailableReadyChoice() {
    if (!this.readyTracked || this.readyStock > 0 || !this.readyRadio) return;

    const choice = this.readyRadio.closest('.uo-purchase-mode__choice');
    if (!choice) return;

    choice.style.minHeight = '54px';
    choice.style.padding = '9px 10px';
    choice.style.border = '1px solid rgb(var(--color-foreground-rgb) / 0.22)';
    choice.style.background = 'transparent';
    choice.style.boxShadow = 'none';
    choice.style.opacity = '0.45';
    choice.style.cursor = 'not-allowed';

    const title = choice.querySelector('.uo-purchase-mode__choice-title');
    if (title) title.textContent = 'No skeins ready to ship';
  }

  rememberCurrentQuantity() {
    if (!this.currentMode || !this.quantityInput) return;
    const value = Number(this.quantityInput.value);
    if (!Number.isFinite(value)) return;

    if (this.currentMode === 'dto') {
      this.dtoValue = Math.max(this.dtoMinimum, value);
    } else {
      this.readyValue = Math.max(1, value);
      this.readyCartQuantity = Number(this.quantityInput.dataset.cartQuantity || this.readyCartQuantity || 0);
    }
  }

  async applyMode(mode) {
    if (!this.form || !this.variantInput || !this.quantityInput) return;
    if (mode === 'ready' && this.readyTracked && this.readyStock <= 0) mode = 'dto';

    this.rememberCurrentQuantity();
    this.currentMode = mode;
    this.dataset.mode = mode;

    if (mode === 'dto') {
      this.dtoRadio.checked = true;
      this.variantInput.value = this.dtoVariantId;
      this.setDtoProperties();
      this.setQuantityConstraints(this.dtoMinimum, null, Math.max(this.dtoMinimum, this.dtoValue), 0);
      this.setAddToCartState(true);
      this.setStickyVisible(false);
      this.formComponent.dataset.quantityDefault = String(this.dtoMinimum);
    } else {
      this.readyRadio.checked = true;
      this.variantInput.value = this.readyVariantId;
      this.removeDtoProperties();
      await this.refreshReadyCartQuantity();
      const max = this.readyTracked ? this.readyStock : null;
      const value = max == null ? this.readyValue : Math.min(Math.max(1, this.readyValue), Math.max(1, max));
      this.setQuantityConstraints(1, max, value, this.readyCartQuantity);
      const canBuyReady = !this.readyTracked || this.readyStock > 0;
      this.setAddToCartState(canBuyReady, canBuyReady ? null : (this.dataset.soldOutLabel || 'Sold out'));
      this.setStickyVisible(true);
      this.formComponent.dataset.quantityDefault = '1';
    }
  }

  setQuantityConstraints(min, max, value, cartQuantity) {
    const minString = String(min);
    const maxString = max == null ? null : String(max);

    this.quantityInput.min = minString;
    this.quantityInput.dataset.min = minString;
    this.quantityInput.step = '1';
    this.quantityInput.dataset.cartQuantity = String(cartQuantity || 0);
    this.quantityInput.disabled = false;

    if (maxString == null) {
      this.quantityInput.removeAttribute('max');
    } else {
      this.quantityInput.max = maxString;
    }

    this.quantitySelector?.setCartQuantity?.(Number(cartQuantity || 0));
    this.quantitySelector?.updateConstraints?.(minString, maxString, '1');
    this.quantitySelector?.setValue?.(value);
    if (!this.quantitySelector?.setValue) this.quantityInput.value = String(value);
  }

  setDtoProperties() {
    this.removeDtoProperties();
    const properties = [
      ['Purchase type', 'Dyed to Order'],
      ['Colorway', this.dataset.colorway || ''],
      ['Base', this.dataset.base || ''],
      ['_Original product', this.dataset.originalProductUrl || ''],
      ['_DTO minimum', String(this.dtoMinimum)],
    ];

    for (const [name, value] of properties) {
      if (!value) continue;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      input.dataset.uoDtoProperty = 'true';
      this.form.appendChild(input);
    }
  }

  removeDtoProperties() {
    this.form?.querySelectorAll('[data-uo-dto-property="true"]').forEach((input) => input.remove());
  }

  getAddToCartLabel() {
    const quantity = Math.max(1, Number(this.quantityInput?.value) || 1);
    const purchaseType = this.currentMode === 'dto' ? 'Dyed to Order' : 'Ready to Ship';
    const unit = quantity === 1 ? 'skein' : 'skeins';

    return `Add ${quantity} ${purchaseType} ${unit}`;
  }

  updateAddToCartLabel() {
    if (!this.addToCartButton || !this.currentMode) return;
    this.setAddToCartLabel(this.getAddToCartLabel());
  }

  setAddToCartLabel(label) {
    if (!this.addToCartButton || !label) return;
    const textNode = Array.from(this.addToCartButton.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0
    );
    if (textNode) textNode.nodeValue = ` ${label} `;
  }

  setAddToCartState(enabled, label = null) {
    if (this.addToCartComponent) {
      if (enabled) this.addToCartComponent.enable?.();
      else this.addToCartComponent.disable?.();
    } else if (this.addToCartButton) {
      this.addToCartButton.disabled = !enabled;
    }

    this.setAddToCartLabel(label || this.getAddToCartLabel());
  }

  setStickyVisible(visible) {
    const sticky = document.querySelector(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"]`);
    if (!sticky) return;
    if (visible) sticky.removeAttribute('hidden');
    else sticky.setAttribute('hidden', '');
  }

  async refreshReadyCartQuantity() {
    try {
      const cartUrl = `${window.Theme?.routes?.cart_url || '/cart'}.js`;
      const response = await fetch(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const readyNumericId = String(this.readyVariantId).split('/').pop();
      const item = cart.items?.find((cartItem) => String(cartItem.variant_id) === readyNumericId);
      this.readyCartQuantity = item ? Number(item.quantity || 0) : 0;
    } catch (_) {
      // The product form still has Shopify's own validation if the cart refresh is unavailable.
    }
  }
}

if (!customElements.get('uo-purchase-mode')) {
  customElements.define('uo-purchase-mode', UOPurchaseMode);
}
